/**
 * Gestionnaire de connexion SignalR simple et clair
 * Une seule responsabilité: maintenir la connexion WebSocket
 */

import * as signalR from '@microsoft/signalr';

// Configuration des URLs
const CHAT_HUB_URL =
  process.env.NEXT_PUBLIC_WS_URL ||
  'https://bingowendake-prod.azurewebsites.net/chatHub';

const GAME_HUB_URL =
  process.env.NEXT_PUBLIC_WS_URL_GAME ||
  'https://bingowendake-prod.azurewebsites.net/livegame';

// Configuration du transport WebSocket
const getWebSocketConfig = () => {
  // Permettre la négociation pour une meilleure compatibilité
  // Si skipNegotiation échoue, SignalR essaiera automatiquement avec négociation
  return {
    // Ne pas forcer skipNegotiation car cela peut causer des problèmes avec certains serveurs
    // Laisser SignalR négocier automatiquement le meilleur transport
    transport: signalR.HttpTransportType.WebSockets,
  };
};

// Connexions globales (singleton)
let chatConnection = null;
let gameConnection = null;

// Fermer les connexions à la fermeture de la page
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (chatConnection) {
      chatConnection.stop().catch(console.error);
    }
    if (gameConnection) {
      gameConnection.stop().catch(console.error);
    }
  });
}

/**
 * Crée et démarre les connexions SignalR
 * @param {Object} options - Options de connexion (userEmail, etc.)
 * @param {Function} onMessageReceived - Callback appelé quand un message est reçu
 * @returns {Promise<Object>} Objet contenant les deux connexions
 */
export async function createConnections(options = {}, onMessageReceived) {
  try {
    // Créer la connexion chatHub
    if (!chatConnection) {
      chatConnection = new signalR.HubConnectionBuilder()
        .withUrl(CHAT_HUB_URL, getWebSocketConfig())
        .withAutomaticReconnect([2000, 4000, 6000, 8000, 10000, 60000])
        .configureLogging(signalR.LogLevel.Warning) // Réduire les logs en production
        .build();

      // Écouter les messages du chatHub
      chatConnection.on('SendGameData', (message) => {
        if (onMessageReceived) {
          onMessageReceived(message);
        }
      });
    }

    // Créer la connexion livegame
    if (!gameConnection) {
      const urlParams = new URLSearchParams({
        appName: 'NEXT_JS_APP',
        userEmail: options.userEmail || '',
      });

      gameConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${GAME_HUB_URL}?${urlParams.toString()}`, getWebSocketConfig())
        .withAutomaticReconnect([2000, 4000, 6000, 8000, 10000, 60000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();
    }

    // Démarrer les connexions si elles ne sont pas déjà démarrées ou en cours de connexion
    if (
      chatConnection.state === signalR.HubConnectionState.Disconnected ||
      chatConnection.state === signalR.HubConnectionState.Disconnecting
    ) {
      try {
        await chatConnection.start();
        await chatConnection.invoke('AddToGroup', 'bingoWendake');
        console.info('ChatHub connecté avec succès');
      } catch (error) {
        // Vérifier si la connexion a réussi entre-temps (race condition)
        if (chatConnection.state === signalR.HubConnectionState.Connected) {
          // La connexion a réussi, continuer normalement
          console.info('ChatHub connecté (état vérifié après erreur)');
        } else if (chatConnection.state === signalR.HubConnectionState.Connecting) {
          // La connexion est en cours, attendre un peu et vérifier à nouveau
          await new Promise((resolve) => setTimeout(resolve, 1000));
          if (chatConnection.state === signalR.HubConnectionState.Connected) {
            console.info('ChatHub connecté après attente');
          } else {
            // La connexion a vraiment échoué, propager l'erreur
            console.error('Erreur lors du démarrage de chatConnection:', error);
            throw error;
          }
        } else {
          // La connexion a vraiment échoué, propager l'erreur
          console.error('Erreur lors du démarrage de chatConnection:', error);
          throw error;
        }
      }
    }

    if (
      gameConnection.state === signalR.HubConnectionState.Disconnected ||
      gameConnection.state === signalR.HubConnectionState.Disconnecting
    ) {
      try {
        await gameConnection.start();
        console.info('GameHub connecté avec succès');
      } catch (error) {
        // Vérifier si la connexion a réussi entre-temps (race condition)
        if (gameConnection.state === signalR.HubConnectionState.Connected) {
          // La connexion a réussi, continuer normalement
          console.info('GameHub connecté (état vérifié après erreur)');
        } else if (gameConnection.state === signalR.HubConnectionState.Connecting) {
          // La connexion est en cours, attendre un peu et vérifier à nouveau
          await new Promise((resolve) => setTimeout(resolve, 1000));
          if (gameConnection.state === signalR.HubConnectionState.Connected) {
            console.info('GameHub connecté après attente');
          } else {
            // La connexion a vraiment échoué, propager l'erreur
            console.error('Erreur lors du démarrage de gameConnection:', error);
            throw error;
          }
        } else {
          // La connexion a vraiment échoué, propager l'erreur
          console.error('Erreur lors du démarrage de gameConnection:', error);
          throw error;
        }
      }
    }

    return {
      chatConnection,
      gameConnection,
    };
  } catch (error) {
    console.error('Erreur lors de la création des connexions:', error);
    throw error;
  }
}

/**
 * Ferme les connexions
 * Note: À utiliser seulement lors de la fermeture de la page
 */
export async function closeConnections() {
  try {
    if (chatConnection && chatConnection.state !== signalR.HubConnectionState.Disconnected) {
      try {
        await chatConnection.stop();
      } catch (error) {
        // Ignorer les erreurs si la connexion est déjà fermée
        console.warn('Erreur lors de l\'arrêt de chatConnection:', error);
      }
      chatConnection = null;
    }
    if (gameConnection && gameConnection.state !== signalR.HubConnectionState.Disconnected) {
      try {
        await gameConnection.stop();
      } catch (error) {
        // Ignorer les erreurs si la connexion est déjà fermée
        console.warn('Erreur lors de l\'arrêt de gameConnection:', error);
      }
      gameConnection = null;
    }
  } catch (error) {
    console.error('Erreur lors de la fermeture des connexions:', error);
  }
}

/**
 * Vérifie si les connexions sont actives
 * @returns {boolean}
 */
export function areConnectionsActive() {
  return (
    chatConnection?.state === signalR.HubConnectionState.Connected &&
    gameConnection?.state === signalR.HubConnectionState.Connected
  );
}

